import { cn } from '@/common/lib/cn/cn'
import { Button } from '@/common/ui/Form/Button'
import { Input } from '@/common/ui/Form/Input'
import type { InputType } from '@/common/ui/Form/Input'
import { Icon } from '@/common/ui/Icon'
import { forwardRef, useCallback, useState, type ChangeEvent, type KeyboardEvent } from 'react'

type InputSize = NonNullable<InputType['size']>

/** На одну ступень меньше поля (у `sm` остаётся `sm`). */
const getClearControlSize = (fieldSize: InputSize): InputSize => {
    if (fieldSize === 'xl') return 'lg'
    if (fieldSize === 'lg') return 'md'
    return 'sm'
}

export type InputSearchProps = Omit<InputType, 'type' | 'afterIcon' | 'afterSlot'> & {
    type?: 'search' | 'text'
    afterIcon?: InputType['afterIcon']
    /**
     * Применённый поиск из URL (например `q` из `useUrlListFilters`).
     * При непустом значении поле получает `active`, как при заданном пропе `active` у `Input`.
     */
    appliedSearch?: string
}

export const InputSearch = forwardRef<HTMLInputElement, InputSearchProps>(
    (
        {
            type = 'search',
            afterIcon = 'search',
            size = 'lg',
            className,
            disabled,
            active,
            appliedSearch,
            value,
            defaultValue,
            onChange,
            ...props
        },
        ref
    ) => {
        const [uncontrolledValue, setUncontrolledValue] = useState(() =>
            defaultValue == null ? '' : String(defaultValue)
        )
        const isControlled = value !== undefined
        const resolvedValue = isControlled ? String(value ?? '') : uncontrolledValue
        const showClear = resolvedValue.length > 0
        const inputActive = Boolean(active) || Boolean(appliedSearch?.trim())
        const clearControlSize = getClearControlSize(size)

        const handleChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                if (!isControlled) {
                    setUncontrolledValue(event.target.value)
                }

                onChange?.(event)
            },
            [isControlled, onChange]
        )

        const handleClearClick = useCallback(() => {
            if (!isControlled) {
                setUncontrolledValue('')
            }

            if (!onChange) {
                return
            }

            const syntheticEvent = {
                target: { value: '' },
                currentTarget: { value: '' },
            } as ChangeEvent<HTMLInputElement>

            onChange(syntheticEvent)
        }, [isControlled, onChange])

        const handleClearKeyDown = useCallback(
            (event: KeyboardEvent<HTMLButtonElement>) => {
                if (event.key !== 'Enter' && event.key !== ' ') {
                    return
                }

                event.preventDefault()
                handleClearClick()
            },
            [handleClearClick]
        )

        const afterSlot = showClear ? (
            <Button
                type='button'
                variant='ghost'
                emphasis='accent-secondary'
                size={size}
                disabled={disabled}
                onClick={handleClearClick}
                onKeyDown={handleClearKeyDown}
                className='shrink-0'
                aria-label='Очистить поиск'
                title='Очистить поиск'
            >
                <Icon name='search-clear' size={clearControlSize} />
            </Button>
        ) : undefined

        return (
            <Input
                ref={ref}
                type={type}
                afterIcon={afterIcon}
                afterSlot={!disabled && afterSlot}
                disabled={disabled}
                size={size}
                active={inputActive}
                className={cn('min-w-[100px] max-w-full shrink px-[10px]!', className)}
                value={isControlled ? value : undefined}
                defaultValue={isControlled ? undefined : defaultValue}
                onChange={handleChange}
                {...props}
            />
        )
    }
)

InputSearch.displayName = 'InputSearch'
